import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import DOMPurify from "dompurify";
import { ArrowLeft, ArrowUpRight, Clock, Loader2, Share2, Linkedin } from "lucide-react";
import { toast } from "sonner";
import { Section, Eyebrow, ButtonPrimary, ButtonGhost } from "../components/ui-kit";
import { BlogCard } from "./Blog";
import { resolveImageUrl } from "../lib/api";
import Seo from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORY_TONE = {
  "Educational Insights": "#FF8C00",
  "Faculty Corner": "#00FFD0",
  "Parent Zone": "#FFB05E",
  "Founder's Journal": "#FF5A00",
};

function formatDate(d) {
  try {
    return new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return "";
  }
}

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [post, setPost] = useState(null);
  const [related, setRelated] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  // Sanitize TipTap-authored HTML before rendering. Memoized so we don't
  // re-run DOMPurify on every re-render of the post page.
  const safeContent = useMemo(
    () => (post?.content ? DOMPurify.sanitize(post.content) : ""),
    [post?.content],
  );

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setNotFound(false);
    (async () => {
      try {
        const { data } = await axios.get(`${API}/blog/posts/${slug}`);
        if (!alive) return;
        setPost(data);
        const rel = await axios
          .get(`${API}/blog/posts`, { params: { category: data.category, exclude_slug: slug, limit: 3 } })
          .then((r) => r.data)
          .catch(() => []);
        if (!alive) return;
        // Pad with most-recent posts from any category if related is short
        if (rel.length < 3) {
          const more = await axios
            .get(`${API}/blog/posts`, { params: { exclude_slug: slug, limit: 6 } })
            .then((r) => r.data)
            .catch(() => []);
          const seen = new Set(rel.map((r) => r.slug));
          for (const m of more) {
            if (rel.length >= 3) break;
            if (!seen.has(m.slug)) rel.push(m);
          }
        }
        if (alive) setRelated(rel.slice(0, 3));
      } catch (err) {
        if (err?.response?.status === 404) setNotFound(true);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => {
      alive = false;
    };
  }, [slug]);

  const onShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({ title: post?.title, url });
      } else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied to clipboard.");
      }
    } catch (e) {
      // navigator.share user-cancel or unsupported clipboard → low-signal
      console.debug("[blog] share aborted:", e);
    }
  };

  const shareOnLinkedIn = () => {
    if (!post) return;
    const base = (process.env.REACT_APP_BACKEND_URL || window.location.origin).replace(/\/$/, "");
    const ogUrl = `${base}/api/share/blog/${post.slug}`;
    const li = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(ogUrl)}`;
    window.open(li, "_blank", "noopener,noreferrer,width=720,height=640");
  };

  if (loading) {
    return (
      <Section>
        <div className="rounded-3xl border border-white/8 bg-[#0C0C10] p-16 flex items-center justify-center text-[#A1A1AA]">
          <Loader2 className="w-5 h-5 animate-spin mr-2" /> Loading the article…
        </div>
      </Section>
    );
  }

  if (notFound || !post) {
    return (
      <Section>
        <div className="max-w-2xl mx-auto text-center py-16">
          <p className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF8C00]">404 · Not Found</p>
          <h1 className="mt-3 font-display font-black text-4xl text-white">This article doesn't exist.</h1>
          <p className="mt-4 text-[#A1A1AA]">It may have been moved, archived, or never published. Head back to the journal.</p>
          <Link to="/blog" className="inline-flex mt-8" data-testid="back-to-blog-404">
            <ButtonPrimary><ArrowLeft className="w-4 h-4" /> Back to Blog</ButtonPrimary>
          </Link>
        </div>
      </Section>
    );
  }

  const tone = CATEGORY_TONE[post.category] || "#FF8C00";
  const canonicalUrl = `https://perforange.com/blog/${post.slug}`;
  const ogImage = post.cover_image
    ? (/^https?:\/\//i.test(post.cover_image) ? post.cover_image : `${(process.env.REACT_APP_BACKEND_URL || "https://perforange.com").replace(/\/$/, "")}${post.cover_image.startsWith("/") ? "" : "/"}${post.cover_image}`)
    : undefined;

  return (
    <article>
      <Seo
        title={post.title}
        description={post.excerpt}
        image={ogImage}
        url={canonicalUrl}
        type="article"
        article={{
          published_time: post.published_at,
          author: post.author,
          section: post.category,
        }}
      />
      {/* Hero */}
      <Section className="!pt-16 relative overflow-hidden">
        <div className="absolute inset-0 bg-grid opacity-25 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto">
          <button
            onClick={() => navigate(-1)}
            data-testid="post-back"
            className="inline-flex items-center gap-2 text-xs font-mono-tech uppercase tracking-widest text-[#A1A1AA] hover:text-white transition-colors mb-8"
          >
            <ArrowLeft className="w-4 h-4" /> Back
          </button>

          <p
            className="text-[11px] font-mono-tech uppercase tracking-[0.25em]"
            style={{ color: tone }}
          >
            {post.category}
          </p>
          <h1
            data-testid="post-title"
            className="mt-4 font-display font-black text-3xl sm:text-4xl md:text-5xl leading-[1.1] tracking-tight text-white"
          >
            {post.title}
          </h1>
          <p className="mt-6 text-lg text-[#D4D4D8] leading-relaxed max-w-3xl">{post.excerpt}</p>

          <div className="mt-7 flex items-center flex-wrap gap-4 text-xs font-mono-tech uppercase tracking-widest text-[#6a6a70]">
            <span className="text-white">{post.author}</span>
            <span className="w-1 h-1 rounded-full bg-[#6a6a70]" />
            <span>{formatDate(post.published_at)}</span>
            <span className="w-1 h-1 rounded-full bg-[#6a6a70]" />
            <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {post.read_minutes} min read</span>
            <button
              onClick={shareOnLinkedIn}
              data-testid="post-share-linkedin"
              className="ml-auto inline-flex items-center gap-1 text-[#A1A1AA] hover:text-[#0a66c2] transition-colors"
              title="Share on LinkedIn"
            >
              <Linkedin className="w-3.5 h-3.5" /> LinkedIn
            </button>
            <button
              onClick={onShare}
              data-testid="post-share"
              className="inline-flex items-center gap-1 text-[#A1A1AA] hover:text-[#FF8C00] transition-colors"
            >
              <Share2 className="w-3.5 h-3.5" /> Share
            </button>
          </div>
        </div>
      </Section>

      {/* Cover */}
      {post.cover_image && (
        <Section className="!pt-0">
          <div className="max-w-5xl mx-auto rounded-2xl overflow-hidden border border-white/8">
            <img src={resolveImageUrl(post.cover_image)} alt={post.title} className="w-full h-auto max-h-[520px] object-cover" />
          </div>
        </Section>
      )}

      {/* Body */}
      <Section className="!pt-0">
        <div
          data-testid="post-content"
          className="prose prose-invert max-w-3xl mx-auto blog-prose"
          dangerouslySetInnerHTML={{ __html: safeContent }}
        />
      </Section>

      {/* CTA strip */}
      <Section className="!pt-0">
        <div className="max-w-3xl mx-auto rounded-2xl border border-[#FF8C00]/30 bg-gradient-to-br from-[#1a1208] via-[#0A0A0C] to-[#0C0C10] p-7 md:p-9 flex flex-col md:flex-row md:items-center md:justify-between gap-5">
          <div>
            <p className="text-[10px] font-mono-tech uppercase tracking-[0.25em] text-[#FF8C00]">Next Step</p>
            <h3 className="mt-2 font-display font-black text-xl md:text-2xl text-white">Want this delivered to your inbox?</h3>
            <p className="mt-1 text-sm text-[#A1A1AA]">Operational reading for the AVGC era. No spam, no fluff.</p>
          </div>
          <Link to="/partnership" data-testid="post-cta-partnership">
            <ButtonGhost>Talk to us <ArrowUpRight className="w-4 h-4" /></ButtonGhost>
          </Link>
        </div>
      </Section>

      {/* Related */}
      {related.length > 0 && (
        <Section className="!pt-0">
          <div className="max-w-6xl mx-auto">
            <div className="mb-8">
              <Eyebrow>Related Reading</Eyebrow>
              <h2 className="font-display font-black text-2xl md:text-3xl text-white">Continue down this track.</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-testid="related-posts">
              {related.map((r) => (
                <BlogCard key={r.slug} post={r} />
              ))}
            </div>
          </div>
        </Section>
      )}

      {/* Blog prose styles (scoped) */}
      <style>{`
        .blog-prose {
          color: #D4D4D8;
          font-family: 'Lato', system-ui, sans-serif;
          font-size: 17px;
          line-height: 1.75;
        }
        .blog-prose h2 {
          font-family: 'Urbanist', system-ui, sans-serif;
          color: #FFFFFF;
          font-weight: 900;
          font-size: 28px;
          line-height: 1.2;
          letter-spacing: -0.01em;
          margin-top: 2.4em;
          margin-bottom: 0.6em;
        }
        .blog-prose h3 {
          font-family: 'Urbanist', system-ui, sans-serif;
          color: #FFFFFF;
          font-weight: 800;
          font-size: 22px;
          margin-top: 2em;
          margin-bottom: 0.5em;
        }
        .blog-prose p { margin: 1.1em 0; }
        .blog-prose em { color: #FF8C00; font-style: normal; }
        .blog-prose strong { color: #FFFFFF; }
        .blog-prose a { color: #FF8C00; text-decoration: underline; text-underline-offset: 3px; }
        .blog-prose a:hover { color: #FFA333; }
        .blog-prose ul, .blog-prose ol { padding-left: 1.4em; margin: 1.1em 0; }
        .blog-prose li { margin: 0.4em 0; }
        .blog-prose ol { list-style: decimal; }
        .blog-prose ul { list-style: disc; }
        .blog-prose blockquote {
          border-left: 3px solid #FF8C00;
          padding: 0.4em 1.1em;
          color: #E4E4E7;
          font-style: italic;
          margin: 1.6em 0;
          background: rgba(255,140,0,0.04);
          border-radius: 0 8px 8px 0;
        }
        .blog-prose code {
          font-family: 'JetBrains Mono', Menlo, monospace;
          background: rgba(255,255,255,0.06);
          border: 1px solid rgba(255,255,255,0.08);
          border-radius: 6px;
          padding: 2px 6px;
          font-size: 0.92em;
        }
      `}</style>
    </article>
  );
}
